import { useQuery, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/lib/pocketbase';

export interface Discount {
  id: string;
  percentage: number;
  is_active: boolean;
  start_date?: string;
  end_date?: string;
  announcement_text: string;
  applies_to: 'all' | 'courses' | 'bookings';
  created: string;
  updated: string;
}

export const useActiveDiscount = () => {
  const queryClient = useQueryClient();
  
  const { data: activeDiscount, isLoading, error } = useQuery({
    queryKey: ['active-discount'],
    queryFn: async () => {
      try {
        console.log('🔍 Fetching active discount from PocketBase...');
        const discounts = await pb.collection('discounts').getList(1, 1, {
          filter: `is_active = true`,
          sort: '-created',
        });

        console.log('📦 Discounts response:', discounts.items.length, 'items');

        if (discounts.items.length > 0) {
          const discount = discounts.items[0] as unknown as Discount;
          console.log('✅ Found discount:', discount.id, 'is_active:', discount.is_active);
          
          // Check date validity if dates are set
          const startValid = !discount.start_date || new Date(discount.start_date) <= new Date();
          const endValid = !discount.end_date || new Date(discount.end_date) >= new Date();
          
          if (startValid && endValid) {
            return discount;
          }
          console.log('⏰ Discount outside valid date range');
        }
        console.log('❌ No active discount found');
        return null;
      } catch (err: any) {
        console.log('No active discount found or collection does not exist:', err.message);
        return null;
      }
    },
    staleTime: 1 * 60 * 1000, // Cache for 1 minute (reduced from 5)
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchInterval: 1 * 60 * 1000, // Refresh every 1 minute (reduced from 5)
  });

  // Function to force refresh the discount status
  const refreshDiscount = () => {
    queryClient.invalidateQueries({ queryKey: ['active-discount'] });
  };

  // Calculate discounted price
  const calculateDiscountedPrice = (originalPrice: number): number => {
    if (!activeDiscount || !originalPrice) return originalPrice;
    const discountMultiplier = 1 - (activeDiscount.percentage / 100);
    return Math.round(originalPrice * discountMultiplier * 100) / 100;
  };

  // Check if discount applies to a specific type
  const appliesToType = (type: 'courses' | 'bookings'): boolean => {
    if (!activeDiscount) return false;
    return activeDiscount.applies_to === 'all' || activeDiscount.applies_to === type;
  };

  return {
    activeDiscount,
    isLoading,
    error: error ? String(error) : null,
    calculateDiscountedPrice,
    appliesToType,
    hasActiveDiscount: !!activeDiscount,
    refreshDiscount,
  };
};
